import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { Plus, X } from 'lucide-react';

const GRADES = { 'A+':4.0,'A':4.0,'A-':3.7,'B+':3.3,'B':3.0,'B-':2.7,'C+':2.3,'C':2.0,'C-':1.7,'D+':1.3,'D':1.0,'F':0 };

export default function GPACalc() {
  const [tab, setTab] = useState('semester');
  return (
    <CalcShell look="cnet"
      title="GPA Calculator"
      subtitle="Compute semester GPA and overall cumulative GPA across multiple terms."
      about={<>
        <p><b>GPA (Grade Point Average)</b> is the weighted average of grade points, where credits act as weights.</p>
        <p>Formula: <b>GPA = Σ(grade points × credits) ÷ Σ(credits)</b>. This calculator uses the standard 4.0 US scale.</p>
      </>}
    >
      <div className="flex gap-2 mb-5 flex-wrap">
        {[{id:'semester',l:'Semester GPA'},{id:'cumulative',l:'Cumulative GPA'},{id:'planning',l:'GPA Planning'}].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === t.id ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'}`}>{t.l}</button>
        ))}
      </div>
      {tab === 'semester' ? <Semester /> : tab === 'cumulative' ? <Cumulative /> : <Planning />}
    </CalcShell>
  );
}

function Semester() {
  const [rows, setRows] = useState([
    { name: 'Math', credit: 3, grade: 'A' },
    { name: 'Physics', credit: 4, grade: 'B+' },
    { name: 'History', credit: 3, grade: 'A-' }
  ]);
  const gpa = useMemo(() => {
    let pts = 0, cr = 0;
    rows.forEach(r => { const p = GRADES[r.grade]; if (p !== undefined) { pts += p * (+r.credit || 0); cr += +r.credit || 0; } });
    return { gpa: cr > 0 ? pts / cr : 0, pts, cr };
  }, [rows]);
  const upd = (i, k, v) => { const nr = [...rows]; nr[i][k] = v; setRows(nr); };
  return (
    <>
      {/* Marksheet-style header */}
      <table className="data-table mb-3">
        <thead><tr><th>#</th><th>Course</th><th>Credits</th><th>Grade</th><th>Points</th><th>Weighted</th><th></th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>{i+1}</td>
              <td><input value={r.name} onChange={e => upd(i, 'name', e.target.value)} className="w-32"/></td>
              <td><input type="number" value={r.credit} onChange={e => upd(i, 'credit', +e.target.value || 0)} className="w-16"/></td>
              <td><select value={r.grade} onChange={e => upd(i, 'grade', e.target.value)}>{Object.keys(GRADES).map(g => <option key={g}>{g}</option>)}</select></td>
              <td>{GRADES[r.grade]?.toFixed(1)}</td>
              <td>{(GRADES[r.grade] * (+r.credit||0)).toFixed(2)}</td>
              <td><button onClick={() => setRows(rows.filter((_, x) => x !== i))} className="text-slate-400 hover:text-red-600"><X className="w-4 h-4" /></button></td>
            </tr>
          ))}
          <tr className="font-semibold"><td colSpan={2}>Total</td><td>{gpa.cr}</td><td></td><td></td><td>{gpa.pts.toFixed(2)}</td><td></td></tr>
        </tbody>
      </table>
      <button onClick={() => setRows([...rows, { name: '', credit: 3, grade: 'A' }])} className="flex items-center gap-1 text-sm text-blue-600 font-medium"><Plus className="w-4 h-4" /> Add course</button>
      <div className="result-panel mt-6"><div className="result-title">Semester GPA</div><div className="result-value">{gpa.gpa.toFixed(3)}</div></div>
      <StepsPanel steps={[
        `Sum grade points × credits = ${gpa.pts.toFixed(2)}`,
        `Total credits = ${gpa.cr}`,
        `GPA = ${gpa.pts.toFixed(2)} / ${gpa.cr} = <b>${gpa.gpa.toFixed(3)}</b>`
      ]} />
    </>
  );
}

function Planning() {
  const [current, setCurrent] = useState(3.2);
  const [currentCr, setCurrentCr] = useState(60);
  const [target, setTarget] = useState(3.5);
  const [nextCr, setNextCr] = useState(15);
  const required = useMemo(() => {
    const totalNeeded = Number(target) * (Number(currentCr) + Number(nextCr));
    const have = Number(current) * Number(currentCr);
    return Number(nextCr) ? (totalNeeded - have) / Number(nextCr) : 0;
  }, [current, currentCr, target, nextCr]);
  return (
    <>
      <p className="text-sm text-slate-600 mb-4">Estimate what GPA you need in your next term to reach a target cumulative GPA.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="field"><label>Current cumulative GPA</label><input type="number" step="0.01" value={current} onChange={e => setCurrent(e.target.value)}/></div>
        <div className="field"><label>Credits completed</label><input type="number" value={currentCr} onChange={e => setCurrentCr(e.target.value)}/></div>
        <div className="field"><label>Target cumulative GPA</label><input type="number" step="0.01" value={target} onChange={e => setTarget(e.target.value)}/></div>
        <div className="field"><label>Credits next term</label><input type="number" value={nextCr} onChange={e => setNextCr(e.target.value)}/></div>
      </div>
      <div className="result-panel mt-4">
        <div className="result-title">GPA needed next term</div>
        <div className="result-value">{required.toFixed(2)}</div>
        {required > 4.0 && <div className="text-sm text-rose-600 mt-1">Target is unreachable in a single term — consider more credits or a lower target.</div>}
      </div>
    </>
  );
}

function Cumulative() {
  const [terms, setTerms] = useState([
    { name: 'Fall 2023', gpa: 3.5, credits: 15 },
    { name: 'Spring 2024', gpa: 3.8, credits: 16 }
  ]);
  const c = useMemo(() => {
    let pts = 0, cr = 0;
    terms.forEach(t => { pts += (+t.gpa || 0) * (+t.credits || 0); cr += +t.credits || 0; });
    return { cgpa: cr > 0 ? pts/cr : 0, cr };
  }, [terms]);
  const upd = (i, k, v) => { const n = [...terms]; n[i][k] = k === 'name' ? v : (+v || 0); setTerms(n); };
  return (
    <>
      <div className="grid grid-cols-12 text-xs font-semibold text-slate-600 mb-2 px-1">
        <div className="col-span-5">Term</div><div className="col-span-3">GPA</div><div className="col-span-3">Credits</div><div></div>
      </div>
      {terms.map((t, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center">
          <input value={t.name} onChange={e => upd(i, 'name', e.target.value)} className="col-span-5 px-3 py-2 border border-slate-200 rounded-lg bg-slate-50" />
          <input type="number" step="0.01" value={t.gpa} onChange={e => upd(i, 'gpa', e.target.value)} className="col-span-3 px-3 py-2 border border-slate-200 rounded-lg bg-slate-50" />
          <input type="number" value={t.credits} onChange={e => upd(i, 'credits', e.target.value)} className="col-span-3 px-3 py-2 border border-slate-200 rounded-lg bg-slate-50" />
          <button onClick={() => setTerms(terms.filter((_, x) => x !== i))} className="text-slate-400 hover:text-red-600"><X className="w-4 h-4" /></button>
        </div>
      ))}
      <button onClick={() => setTerms([...terms, { name: '', gpa: 0, credits: 0 }])} className="flex items-center gap-1 text-sm text-blue-600 font-medium"><Plus className="w-4 h-4" /> Add term</button>
      <div className="result-panel mt-6"><div className="result-title">Cumulative GPA</div><div className="result-value">{c.cgpa.toFixed(3)}</div><div className="text-sm text-slate-600 mt-1">Total credits: {c.cr}</div></div>
    </>
  );
}
